import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ImageUp, Type, X } from "lucide-react";

export default function AIPaperChecker() {
  const [mode, setMode] = useState("text"); // text | vision
  const [form, setForm] = useState({ question: "", student_answer: "", expected: "" });
  const [imageB64, setImageB64] = useState("");
  const [imageMime, setImageMime] = useState("image/png");
  const [imagePreview, setImagePreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    setImageMime(f.type);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      setImagePreview(dataUrl);
      const b64 = dataUrl.split(",")[1] || "";
      setImageB64(b64);
    };
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.question) return toast.error("Question is required");
    setLoading(true); setResult(null);
    try {
      if (mode === "vision") {
        if (!imageB64) return toast.error("Upload a paper image");
        const { data } = await api.post("/ai/paper-check-vision", {
          question: form.question,
          expected: form.expected,
          image_b64: imageB64,
          mime: imageMime,
        });
        setResult(data.result);
      } else {
        if (!form.student_answer) return toast.error("Student answer required");
        const { data } = await api.post("/ai/paper-check", form);
        setResult(data.result);
      }
      toast.success("Checked");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="paper-checker-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ ai paper checker</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">Grade a student answer</h1>
        <p className="text-sm text-neutral-600 mt-1">Text grading: 3 credits · Handwritten image (vision): 5 credits.</p>
      </div>

      <div className="inline-flex border border-neutral-300 bg-white">
        <button
          data-testid="mode-text-btn"
          onClick={() => setMode("text")}
          className={`px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 ${mode === "text" ? "bg-[#0A0C10] text-white" : "text-neutral-600"}`}
        ><Type size={12} /> Typed answer</button>
        <button
          data-testid="mode-vision-btn"
          onClick={() => setMode("vision")}
          className={`px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5 ${mode === "vision" ? "bg-[#0A0C10] text-white" : "text-neutral-600"}`}
        ><ImageUp size={12} /> Handwritten image</button>
      </div>

      <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 space-y-3">
        <textarea className={inp} rows={2} placeholder="Question *" required value={form.question} onChange={set("question")} data-testid="paper-question" />

        {mode === "text" ? (
          <textarea className={inp} rows={4} placeholder="Student's answer *" required value={form.student_answer} onChange={set("student_answer")} data-testid="paper-answer" />
        ) : (
          <div className="border border-dashed border-neutral-300 p-4 bg-neutral-50">
            {imagePreview ? (
              <div className="relative inline-block">
                <img src={imagePreview} alt="paper" className="max-h-72 border border-neutral-200" />
                <button type="button" onClick={() => { setImageB64(""); setImagePreview(""); if (fileRef.current) fileRef.current.value = ""; }} className="absolute -top-2 -right-2 bg-black text-white rounded-full p-1"><X size={12} /></button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center py-8 cursor-pointer text-neutral-500 hover:text-[#002FA7]" data-testid="paper-upload-zone">
                <ImageUp size={28} />
                <span className="mt-2 text-sm">Click to upload student's handwritten answer</span>
                <span className="text-xs text-neutral-400 mt-1">JPG, PNG, HEIC — clear photo works best</span>
                <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="paper-image-input" />
              </label>
            )}
          </div>
        )}

        <textarea className={inp} rows={2} placeholder="Expected answer (optional)" value={form.expected} onChange={set("expected")} />

        <button type="submit" disabled={loading} data-testid="paper-submit" className="bg-[#0A0C10] text-white font-bold py-3 px-6 inline-flex items-center gap-2 disabled:opacity-60">
          <Sparkles size={16} /> {loading ? "Grading…" : mode === "vision" ? "Grade handwritten answer" : "Grade with AI"}
        </button>
      </form>

      {result && (
        <div className="bg-white border border-neutral-200 p-6 space-y-3" data-testid="paper-result">
          <div className="flex items-center justify-between">
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ result</div>
            <div className={`text-3xl font-display font-bold ${result.correct ? "text-green-700" : "text-red-600"}`}>{result.score ?? "—"}/10</div>
          </div>
          {result.transcript && <Row k="Transcribed" v={result.transcript} />}
          <Row k="Verdict" v={result.correct ? "Correct" : "Needs work"} />
          <Row k="Feedback" v={result.feedback} />
          {result.mistakes?.length > 0 && <Row k="Mistakes" v={(result.mistakes || []).join("; ")} />}
          {result.weak_concepts?.length > 0 && <Row k="Weak concepts" v={(result.weak_concepts || []).join(", ")} />}
          {result.improvement_plan && <Row k="Plan" v={result.improvement_plan} />}
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 py-2 border-t border-neutral-100">
      <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">{k}</div>
      <div className="sm:col-span-3 text-sm text-neutral-700">{v}</div>
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";
